import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/authApi';
import './EngineerDashboard.css';

function EngineerDashboard() {
  const navigate = useNavigate();
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [perm, setPerm] = useState(null);
  const [permLoading, setPermLoading] = useState(true);

  useEffect(() => {
    fetchPermissions();
    fetchConcerns();
  }, []);

  const fetchPermissions = async () => {
    try {
      setPermLoading(true);
      
      // Get user info from localStorage
      const userInfoString = localStorage.getItem('user_info');
      
      if (!userInfoString) {
        console.error('No user data in localStorage');
        setPermLoading(false);
        return;
      }

      const userInfo = JSON.parse(userInfoString);
      const engineerEmail = userInfo.email;
      console.log("Engineer Email is", engineerEmail);

      // Use authenticated API call
      const response = await api.get(`/users/permissions/${engineerEmail}`);
      const data = await response.json();
      
      setPerm(data.Message[0][0]);
      console.log("Permission level:", data.Message[0][0]);
      setPermLoading(false);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setPerm(0); // Default to no permissions on error
      setPermLoading(false);
    }
  };

  const fetchConcerns = async () => {
    try {
      setLoading(true);
      
      // Use authenticated API call
          console.log('Fetching concerns...');
          const response = await api.get('/concerns/all');
          console.log('Response status:', response.status);
          console.log('Response ok:', response.ok);
          
          const data = await response.json();
          console.log('Concerns data:', data);
      
      // Transform the data (adjust based on your data structure)
      const transformedConcerns = data.Message.map(concern => ({
        id: concern[0],
        status: concern[8],
        // Add other fields as needed
      }));
      
      setConcerns(transformedConcerns);
      setError(null);
    } catch (err) {
      setError('Failed to load concerns. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear all localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user_info');
    navigate('/login');
  };

  const pendingCount = concerns.filter(c => c.status === 'Pending').length;
  const totalCount = concerns.length;
  const inProgressCount = concerns.filter(c => c.status === 'In Progress').length;
  
  if (loading || permLoading) {
    return (
      <div className="dashboard">
        <nav className="dashboard-nav">
          <div className="container nav-content">
            <h2 className="logo">EngineeringCubes</h2>
            <button onClick={handleLogout} className="btn btn-secondary">
              Logout
            </button>
          </div>
        </nav>
        <div className="dashboard-content">
          <div className="container">
            <div className="loading-message">Loading dashboard...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <nav className="dashboard-nav">
          <div className="container nav-content">
            <h2 className="logo">EngineeringCubes</h2>
            <button onClick={handleLogout} className="btn btn-secondary">
              Logout
            </button>
          </div>
        </nav>
        <div className="dashboard-content">
          <div className="container">
            <div className="error-message">
              {error}
              <button onClick={fetchConcerns} className="btn btn-primary" style={{ marginTop: '1rem' }}>
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <nav className="dashboard-nav">
        <div className="container nav-content">
          <h2 className="logo">EngineeringCubes</h2>
          <button onClick={handleLogout} className="btn btn-secondary">
            Logout
          </button>
        </div>
      </nav>

      <div className="dashboard-content">
        <div className="container">
          <h1 className="dashboard-title">Engineer Dashboard</h1>
          <p className="dashboard-subtitle">Review and manage client concerns</p>

          <div className="dashboard-grid">
            <div className="dashboard-card">
              <h3>Pending Reviews</h3>
              <p>New concerns awaiting your review</p>
              <div className="stat-number">{pendingCount}</div>
              <button 
                onClick={() => navigate('/pending-reviews')} 
                className="btn btn-primary"
              >
                Review Now
              </button>
            </div>

            <div className="dashboard-card">
              <h3>All Concerns</h3>
              <p>View complete list of all submitted concerns</p>
              <div className="stat-number">{totalCount}</div>
              <button 
                onClick={() => navigate('/all-concerns')} 
                className="btn btn-secondary"
              >
                View All
              </button>
            </div>
            
            <div className="dashboard-card">
              <h3>In Progress</h3>
              <p>Concerns currently being worked on</p>
              <div className="stat-number">{inProgressCount}</div>
              <button 
                onClick={() => navigate('/in-progress')} 
                className="btn btn-primary"
              >
                View In Progress
              </button>
            </div>

            {/* Only show Add Client User if permission level is 1 */}
            {perm === 1 && (
              <div className="dashboard-card">
                <h3>Add Client User</h3>
                <p>Create new client account for project</p>
                <div className="stat-number">
                  <span className="add-user-icon">+</span>
                </div>
                <button 
                  onClick={() => navigate('/add-client')} 
                  className="btn btn-primary"
                >
                  Add New Client
                </button>
              </div>
            )}

            {/* Only show Add Project if permission level is 1 */}
            {perm === 1 && (
              <div className="dashboard-card">
                <h3>Add Project</h3>
                <p>Add new project for existing client</p>
                <div className="stat-number">
                  <span className="add-user-icon">+</span>
                </div>
                <button 
                  onClick={() => navigate('/add-project')} 
                  className="btn btn-primary"
                >
                  Add Project
                </button>
              </div>
            )}

            {/* Only show Audit Logs if permission level is 1 */}
            {perm === 1 && (
              <div className="dashboard-card">
                <h3>Audit Logs</h3>
                <p>View system activity and change history</p>
                <div className="stat-number">
                  <span className="add-user-icon">📋</span>
                </div>
                <button 
                  onClick={() => navigate('/audit-logs')} 
                  className="btn btn-secondary"
                >
                  View Audit Logs
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EngineerDashboard;